import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { logger } from './logger.js';
import { outletDocRepoFor } from './repositories/outlet-repos.js';

/**
 * Active-outlet plumbing for the dashboard.
 *
 * One operator may have multiple outlets. We persist the last selection in
 * localStorage per tenant so the queue board doesn't snap back to outlet[0]
 * on every page load.
 *
 * Resolution:
 *   1. localStorage["kilat.active-outlet.{tenantId}"] if it points to an outlet that still exists
 *   2. Otherwise the first outlet in `outlets`
 *   3. Otherwise null (operator has zero outlets — handled by the UI)
 *
 * The outlets list itself is live-subscribed via the repo's listSnapshot so a
 * newly-provisioned outlet appears in the switcher within seconds.
 */

export interface OutletLite {
  readonly id: string;
  readonly name: string;
}

/**
 * Read shape of a `tenants/{tid}/outlets/{oid}` doc as surfaced by the repo's
 * `listSnapshot` (which stamps the doc `id` on). The repo schema types
 * `profile` as `unknown`, so we narrow to the one field we render here.
 */
interface OutletDocShape {
  readonly id: string;
  readonly profile: { readonly name?: string } | undefined;
}

interface ActiveOutletState {
  readonly outlets: OutletLite[];
  readonly outletId: string | null;
  readonly setOutletId: (id: string) => void;
  readonly loading: boolean;
  /**
   * The outlets list could not be loaded. Without this a refused or broken
   * subscription left `loading` true forever: the queue sat on its skeleton
   * with no error and no way out.
   */
  readonly error: boolean;
  /** Re-subscribe after an error. */
  readonly retry: () => void;
}

const ActiveOutletContext = createContext<ActiveOutletState | null>(null);

function storageKey(tenantId: string): string {
  return `kilat.active-outlet.${tenantId}`;
}

function safeReadStorage(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeWriteStorage(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // localStorage disabled / quota — non-fatal; selection just won't persist.
  }
}

export function ActiveOutletProvider({
  tenantId,
  children,
}: {
  readonly tenantId: string | null;
  readonly children: ReactNode;
}) {
  const [outlets, setOutlets] = useState<OutletLite[]>([]);
  const [outletId, setOutletIdState] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  useEffect(() => {
    if (!tenantId) {
      setOutlets([]);
      setOutletIdState(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(false);
    const repo = outletDocRepoFor(tenantId);
    const unsub = repo.listSnapshot(
      (docs) => {
        const lite: OutletLite[] = docs.map((d) => {
          const { id, profile } = d as OutletDocShape;
          return { id, name: profile?.name ?? id };
        });
        setOutlets(lite);

        // Resolve the active outlet:
        const stored = safeReadStorage(storageKey(tenantId));
        const storedExists = stored && lite.some((o) => o.id === stored);
        const next = storedExists ? stored : (lite[0]?.id ?? null);
        setOutletIdState(next);
        setLoading(false);
      },
      (err) => {
        logger.warn('active outlet: outlets list failed', { tenantId, error: String(err) });
        setError(true);
        setLoading(false);
      },
    );
    return () => {
      unsub();
    };
  }, [tenantId, attempt]);

  const setOutletId = useCallback(
    (id: string) => {
      if (!tenantId) return;
      setOutletIdState(id);
      safeWriteStorage(storageKey(tenantId), id);
      logger.info('active outlet changed', { tenantId, outletId: id });
    },
    [tenantId],
  );

  const value = useMemo<ActiveOutletState>(
    () => ({ outlets, outletId, setOutletId, loading, error, retry }),
    [outlets, outletId, setOutletId, loading, error, retry],
  );

  return <ActiveOutletContext.Provider value={value}>{children}</ActiveOutletContext.Provider>;
}

export function useActiveOutlet(): ActiveOutletState {
  const v = useContext(ActiveOutletContext);
  if (!v) throw new Error('useActiveOutlet must be used inside <ActiveOutletProvider>');
  return v;
}
